import React from 'react';
import './printStyles.css';

const PrintComponent = React.forwardRef(({ reportData, filters }, ref) => {
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    };

    const formatCurrency = (amount) => {
        return `Rs ${parseFloat(amount || 0).toFixed(2)}`;
    };

    if (!reportData) {
        return <div ref={ref}>No data available for printing</div>;
    }

    return (
        <div ref={ref} className="print-container">
            {/* Header */}
            <div className="print-header">
                <h1>Time Sales Report</h1>
                <div className="print-info">
                    <p><strong>Outlet:</strong> {filters.branchName || 'All'}</p>
                    <p><strong>Date Range:</strong> {formatDate(filters.startDate)} to {formatDate(filters.endDate)}</p>
                    {filters.shift && <p><strong>Shift:</strong> {filters.shift}</p>}
                    {filters.timeRange && <p><strong>Time Range:</strong> {filters.timeRange}</p>}
                    {filters.orderType && <p><strong>Order Type:</strong> {filters.orderType}</p>}
                    <p><strong>Generated:</strong> {new Date().toLocaleString()}</p>
                </div>
            </div>

            {/* Metrics Summary */}
            <div className="print-section">
                <h2>Summary Metrics</h2>
                <table className="print-table">
                    <thead>
                        <tr>
                            <th>Metric</th>
                            <th>Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Total Sales</td>
                            <td>{formatCurrency(reportData.totalSales)}</td>
                        </tr>
                        <tr>
                            <td>Total Bills</td>
                            <td>{reportData.totalBills || 0}</td>
                        </tr>
                        <tr>
                            <td>Average Bill Value</td>
                            <td>{formatCurrency(reportData.avgBillValue)}</td>
                        </tr>
                        <tr>
                            <td>Top Performing Time</td>
                            <td>{reportData.topPerformingTime || 'N/A'}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Slot Summary */}
            {reportData.slotSummary && reportData.slotSummary.length > 0 && (
                <div className="print-section">
                    <h2>Time Slot Summary</h2>
                    <table className="print-table">
                        <thead>
                            <tr>
                                <th>Time Slot</th>
                                <th>Bills</th>
                                <th>Avg. Value</th>
                                <th>Gross Sale</th>
                                <th>Net Sale</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.slotSummary.map((slot, index) => (
                                <tr key={index}>
                                    <td>{slot.slot}</td>
                                    <td>{slot.bills}</td>
                                    <td>{formatCurrency(slot.avgValue)}</td>
                                    <td>{formatCurrency(slot.grossSale)}</td>
                                    <td>{formatCurrency(slot.netSale || slot.grossSale)}</td>
                                </tr>
                            ))}
                        </tbody>
                        {reportData.slotSummaryTotal && (
                            <tfoot>
                                <tr>
                                    <td><strong>Total</strong></td>
                                    <td><strong>{reportData.slotSummaryTotal.totalBillSum}</strong></td>
                                    <td><strong>{formatCurrency(reportData.slotSummaryTotal.totalAvgValueSum)}</strong></td>
                                    <td><strong>{formatCurrency(reportData.slotSummaryTotal.totalGrossSaleSum)}</strong></td>
                                    <td><strong>{formatCurrency(reportData.slotSummaryTotal.totalGrossSaleSum)}</strong></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            )}

            {/* Top Products */}
            {reportData.topProducts && reportData.topProducts.length > 0 && (
                <div className="print-section">
                    <h2>Top Selling Products</h2>
                    <table className="print-table">
                        <thead>
                            <tr>
                                <th>Time Slot</th>
                                <th>Product</th>
                                <th>Qty</th>
                                <th>UOM</th>
                                <th>Net Sale</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.topProducts.map((product, index) => (
                                <tr key={index}>
                                    <td>{product.timeSlot}</td>
                                    <td>{product.productName}</td>
                                    <td>{product.qty}</td>
                                    <td>{product.uomCat}</td>
                                    <td>{formatCurrency(product.netSale)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Report Type Data */}
            {reportData.reportTypeData && (
                <div className="print-section">
                    <h2>Order Type Analysis</h2>
                    <table className="print-table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.reportTypeData.dineInSale !== undefined && (
                                <>
                                    <tr>
                                        <td>Dine In Sale</td>
                                        <td>{formatCurrency(reportData.reportTypeData.dineInSale)}</td>
                                    </tr>
                                    <tr>
                                        <td>Take Away Sale</td>
                                        <td>{formatCurrency(reportData.reportTypeData.takeAwaySale)}</td>
                                    </tr>
                                    <tr>
                                        <td>Delivery Sale</td>
                                        <td>{formatCurrency(reportData.reportTypeData.deliverySale)}</td>
                                    </tr>
                                </>
                            )}
                            {reportData.reportTypeData.dineInTxn !== undefined && (
                                <>
                                    <tr>
                                        <td>Dine In Transactions</td>
                                        <td>{reportData.reportTypeData.dineInTxn}</td>
                                    </tr>
                                    <tr>
                                        <td>Take Away Transactions</td>
                                        <td>{reportData.reportTypeData.takeAwayTxn}</td>
                                    </tr>
                                    <tr>
                                        <td>Delivery Transactions</td>
                                        <td>{reportData.reportTypeData.deliveryTxn}</td>
                                    </tr>
                                </>
                            )}
                            {reportData.reportTypeData.averagePerCustomer !== undefined && (
                                <>
                                    <tr>
                                        <td>Average Per Customer</td>
                                        <td>{formatCurrency(reportData.reportTypeData.averagePerCustomer)}</td>
                                    </tr>
                                    <tr>
                                        <td>Dine In APC</td>
                                        <td>{formatCurrency(reportData.reportTypeData.dineInAPC)}</td>
                                    </tr>
                                    <tr>
                                        <td>Take Away APC</td>
                                        <td>{formatCurrency(reportData.reportTypeData.takeAwayAPC)}</td>
                                    </tr>
                                    <tr>
                                        <td>Delivery APC</td>
                                        <td>{formatCurrency(reportData.reportTypeData.deliveryAPC)}</td>
                                    </tr>
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Footer */}
            <div className="print-footer">
                <p>End of Report</p>
            </div>
        </div>
    );
});

PrintComponent.displayName = 'PrintComponent';

export default PrintComponent;
